import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import { isCentralRole } from './authSlice';
import { accountErrorMessage } from '../../utils/accountErrors';
import { normalizeEmail, OPERATOR_ACCOUNT_ROLES } from '../../utils/portalAccounts';

const CENTRAL_ACCOUNT_ROLES = ['admin', 'dispatcher', 'central_admin', 'central_dispatch'];

const normalizeOperator = (operatorDoc) => ({
  id: operatorDoc.id,
  ...operatorDoc.data()
});

export const fetchOperators = createAsyncThunk(
  'operators/fetchOperators',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      if (isCentralRole(currentUser)) {
        const operatorsQuery = query(collection(db, 'operators'), orderBy('name', 'asc'));
        const snapshot = await getDocs(operatorsQuery);
        return snapshot.docs.map(normalizeOperator);
      } else if (currentUser.operatorId) {
        const operatorDoc = await getDoc(doc(db, 'operators', currentUser.operatorId));
        return operatorDoc.exists() ? [normalizeOperator(operatorDoc)] : [];
      } else {
        return [];
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const byNameOrEmail = (a, b) =>
  (a.name || a.email || '').localeCompare(b.name || b.email || '');

/**
 * Alle Unternehmer-Zugänge in EINER Abfrage laden und nach operatorId gruppieren.
 *
 * Vorher lief je Unternehmen eine eigene Query — und weil die Übersicht nach
 * jedem Speichern `fetchOperators` neu ausführt, wiederholten sich alle N
 * Abfragen bei jedem Vorgang. Bei einem Verbund mit 30 Unternehmen waren das 30
 * Queries pro Klick. Dieselbe Sammel-Query nutzt `fetchCentralAccounts` bereits.
 */
export const fetchOperatorUsers = createAsyncThunk(
  'operators/fetchOperatorUsers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;

      // Unternehmer-Konten dürfen nach den Rules nur ihre eigenen Zugänge lesen —
      // für sie bleibt es bei der gezielten Abfrage auf den eigenen Operator.
      const usersQuery = isCentralRole(currentUser)
        ? query(collection(db, 'users'), where('role', 'in', OPERATOR_ACCOUNT_ROLES))
        : query(collection(db, 'users'), where('operatorId', '==', currentUser?.operatorId || '__none__'));

      const snapshot = await getDocs(usersQuery);
      const usersByOperatorId = {};
      snapshot.docs
        .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
        // hiddenFromOperatorsPage blendet technische/Support-Konten aus.
        .filter(user =>
          OPERATOR_ACCOUNT_ROLES.includes(user.role) &&
          user.hiddenFromOperatorsPage !== true &&
          user.operatorId
        )
        .sort(byNameOrEmail)
        .forEach(user => {
          if (!usersByOperatorId[user.operatorId]) usersByOperatorId[user.operatorId] = [];
          usersByOperatorId[user.operatorId].push(user);
        });

      return usersByOperatorId;
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Unternehmer-Zugänge konnten nicht geladen werden.'));
    }
  }
);

export const fetchCentralAccounts = createAsyncThunk(
  'operators/fetchCentralAccounts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins können Konten der Hauptzentrale laden.');
      }

      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', CENTRAL_ACCOUNT_ROLES)
      );
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs
        .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
        // hiddenFromOperatorsPage: optionales Boolean im users-Dokument, um
        // technische/Support-Admins aus „Unternehmen & Sub-Konten" auszublenden.
        .filter(user => user.isDriver !== true && !user.operatorId && user.hiddenFromOperatorsPage !== true)
        .sort(byNameOrEmail);
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Konten der Hauptzentrale konnten nicht geladen werden.'));
    }
  }
);

// Fahrzeuglimit robust normalisieren: nur nicht-negative Ganzzahlen zulassen
// (Dezimal-/Negativwerte oder ungültige Eingaben werden auf 0 abgefangen).
const sanitizeVehicleLimit = (value) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * Unternehmen anlegen oder ändern — über die CF `saveOperator`.
 *
 * Das Fahrzeugkontingent muss gegen die Summe aller vergebenen Kontingente und
 * den Lizenzrahmen geprüft werden. Diese Summe kennt nur der Server, und
 * Firestore-Rules können nicht über eine Collection aggregieren — die Rules
 * sperren Client-Schreiber auf `operators` deshalb vollständig.
 */
const saveOperatorThunk = async (operatorData, { getState, rejectWithValue }) => {
  try {
    const currentUser = getState().auth.user;
    if (!isCentralRole(currentUser)) {
      throw new Error('Nur zentrale Admins können Unternehmen anlegen oder ändern.');
    }

    const saveOperatorFn = httpsCallable(functions, 'saveOperator');
    const result = await saveOperatorFn({
      ...operatorData,
      email: normalizeEmail(operatorData.email),
      vehicleLimit: sanitizeVehicleLimit(operatorData.vehicleLimit)
    });

    return { id: result.data?.id, vehicleLimit: result.data?.vehicleLimit };
  } catch (error) {
    return rejectWithValue(accountErrorMessage(error, 'Das Unternehmen konnte nicht gespeichert werden.'));
  }
};

export const createOperator = createAsyncThunk(
  'operators/createOperator',
  async (operatorData, thunkApi) => saveOperatorThunk({ ...operatorData, id: null }, thunkApi)
);

export const updateOperator = createAsyncThunk(
  'operators/updateOperator',
  async (operatorData, thunkApi) => saveOperatorThunk(operatorData, thunkApi)
);

/**
 * Portalzugang anlegen — Zentral-Konto oder Unternehmer-Zugang.
 *
 * Läuft über die Cloud Function `createPortalAccount` (Admin SDK). Vorher legte
 * das Portal den Auth-Account client-seitig über eine zweite App-Instanz an und
 * schrieb das users-Dokument danach separat. Brach der zweite Schritt ab, blieb
 * ein Auth-Konto ohne Rolle zurück: Login unmöglich, Neuanlage unmöglich
 * (E-Mail belegt), im Portal nicht sichtbar. Die CF legt beides an und rollt im
 * Fehlerfall den Auth-Account zurück.
 */
const createPortalAccountThunk = async (
  { operatorId = null, email, password, name, phone, role },
  { getState, rejectWithValue }
) => {
  try {
    const currentUser = getState().auth.user;
    if (!isCentralRole(currentUser)) {
      throw new Error('Nur zentrale Admins können Portalzugänge anlegen.');
    }

    const createPortalAccountFn = httpsCallable(functions, 'createPortalAccount');
    const result = await createPortalAccountFn({
      operatorId,
      email: normalizeEmail(email),
      password,
      name: (name || '').trim(),
      phone: (phone || '').trim(),
      role
    });

    return {
      id: result.data?.userId,
      email: result.data?.email || normalizeEmail(email),
      name: (name || '').trim(),
      role,
      operatorId
    };
  } catch (error) {
    return rejectWithValue(accountErrorMessage(error, 'Der Zugang konnte nicht angelegt werden.'));
  }
};

export const createCentralAccount = createAsyncThunk(
  'operators/createCentralAccount',
  async ({ role = 'dispatcher', ...rest }, thunkApi) => {
    const result = await createPortalAccountThunk({ ...rest, role, operatorId: null }, thunkApi);
    if (!result?.id) return result;
    thunkApi.dispatch(fetchCentralAccounts());
    return result;
  }
);

export const createOperatorSubAccount = createAsyncThunk(
  'operators/createOperatorSubAccount',
  async ({ role = 'operator_admin', ...rest }, thunkApi) => {
    const result = await createPortalAccountThunk({ ...rest, role }, thunkApi);
    if (!result?.id) return result;
    thunkApi.dispatch(fetchOperatorUsers());
    return result;
  }
);

/**
 * Stammdaten eines Portalzugangs ändern (Name, Telefon, Rolle, E-Mail).
 *
 * Auch das läuft über eine CF: Die E-Mail muss in Firebase Auth UND im
 * users-Dokument geändert werden. Liefe es nur gegen Firestore, zeigte das
 * Portal eine Adresse an, mit der die Anmeldung fehlschlägt.
 */
export const updatePortalAccount = createAsyncThunk(
  'operators/updatePortalAccount',
  async ({ userId, name, phone, role, email, isCentralAccount }, { dispatch, rejectWithValue }) => {
    try {
      const updatePortalAccountFn = httpsCallable(functions, 'updatePortalAccount');
      await updatePortalAccountFn({
        userId,
        name: (name || '').trim(),
        phone: (phone || '').trim(),
        role,
        email: normalizeEmail(email)
      });

      dispatch(isCentralAccount ? fetchCentralAccounts() : fetchOperatorUsers());
      return { userId };
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Der Zugang konnte nicht geändert werden.'));
    }
  }
);

/**
 * Portalzugang endgültig löschen (Auth-Account + users-Dokument).
 * Wie beim Fahrer erst nach dem Deaktivieren — die CF prüft das verbindlich.
 */
export const deletePortalAccount = createAsyncThunk(
  'operators/deletePortalAccount',
  async ({ userId, isCentralAccount }, { dispatch, rejectWithValue }) => {
    try {
      const deletePortalAccountFn = httpsCallable(functions, 'deletePortalAccount');
      await deletePortalAccountFn({ userId });

      dispatch(isCentralAccount ? fetchCentralAccounts() : fetchOperatorUsers());
      return { userId };
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Der Zugang konnte nicht gelöscht werden.'));
    }
  }
);

export const setCentralAccountActive = createAsyncThunk(
  'operators/setCentralAccountActive',
  async ({ userId, active }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins können Konten der Hauptzentrale bearbeiten.');
      }
      if (currentUser.uid === userId && active === false) {
        throw new Error('Das eigene Konto kann hier nicht deaktiviert werden.');
      }

      await updateDoc(doc(db, 'users', userId), {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      dispatch(fetchCentralAccounts());
      return { userId, active };
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Der Status konnte nicht geändert werden.'));
    }
  }
);

// Setzt das Passwort eines Portalzugangs über die Cloud Function
// setOperatorAccountPassword (Admin SDK; nur zentrale Rollen, Zentral-Konten
// nur durch einen Admin). Das Passwort wird NICHT in Firestore gespeichert.
export const resetSubAccountPassword = createAsyncThunk(
  'operators/resetSubAccountPassword',
  async ({ userId, newPassword }, { rejectWithValue }) => {
    try {
      const setOperatorAccountPasswordFn = httpsCallable(functions, 'setOperatorAccountPassword');
      await setOperatorAccountPasswordFn({ userId, newPassword });
      return { userId };
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Portal-Passworts:', error);
      return rejectWithValue(accountErrorMessage(error, 'Das Passwort konnte nicht gesetzt werden.'));
    }
  }
);

export const setOperatorSubAccountActive = createAsyncThunk(
  'operators/setOperatorSubAccountActive',
  async ({ userId, active }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins können Unternehmer-Zugänge bearbeiten.');
      }

      await updateDoc(doc(db, 'users', userId), {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      dispatch(fetchOperatorUsers());
      return { userId, active };
    } catch (error) {
      return rejectWithValue(accountErrorMessage(error, 'Der Status konnte nicht geändert werden.'));
    }
  }
);

// Alle Thunks, die den Speichern-Zustand steuern. Als Liste statt als drei
// getrennte Aufzählungen von `.type`-Strings — sonst wird beim Ergänzen eines
// Thunks regelmäßig einer der drei Zweige vergessen.
const SAVING_THUNKS = [
  createOperator,
  updateOperator,
  createCentralAccount,
  createOperatorSubAccount,
  updatePortalAccount,
  deletePortalAccount,
  setCentralAccountActive,
  setOperatorSubAccountActive
];

const operatorsSlice = createSlice({
  name: 'operators',
  initialState: {
    operators: [],
    usersByOperatorId: {},
    centralAccounts: [],
    isLoading: false,
    isSaving: false,
    error: null
  },
  reducers: {
    clearOperatorsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperators.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOperators.fulfilled, (state, action) => {
        state.isLoading = false;
        state.operators = action.payload;
      })
      .addCase(fetchOperators.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Sammel-Query: das Ergebnis ersetzt die Gruppierung vollständig, damit
      // gelöschte Zugänge nicht als Leiche im State stehen bleiben.
      .addCase(fetchOperatorUsers.fulfilled, (state, action) => {
        state.usersByOperatorId = action.payload;
      })
      .addCase(fetchOperatorUsers.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchCentralAccounts.fulfilled, (state, action) => {
        state.centralAccounts = action.payload;
      })
      .addCase(fetchCentralAccounts.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addMatcher(
        (action) => SAVING_THUNKS.some(thunk => action.type === thunk.pending.type),
        (state) => {
          state.isSaving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => SAVING_THUNKS.some(thunk => action.type === thunk.fulfilled.type),
        (state) => {
          state.isSaving = false;
        }
      )
      .addMatcher(
        (action) => SAVING_THUNKS.some(thunk => action.type === thunk.rejected.type),
        (state, action) => {
          state.isSaving = false;
          state.error = action.payload;
        }
      );
  }
});

export const { clearOperatorsError } = operatorsSlice.actions;
export default operatorsSlice.reducer;
